import { useReducer, useCallback, useTransition } from 'react';
import { useTranslate } from '@shopify/ui-extensions-react/customer-account';
import {
  uploadFiles,
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE,
} from '../utils/fileUpload';
import { UseFileUploadReturn, UseFileUploadOptions, SeatUploadRequirements, UploadableFile } from '../types';
import {
  fileUploadReducer,
  initialFileUploadState,
} from '../reducers/fileUploadReducer';
import { logger } from '../utils/logger';

export const useFileUpload = (options: UseFileUploadOptions = {}): UseFileUploadReturn => {
  const translate = useTranslate();
  const [state, dispatch] = useReducer(fileUploadReducer, initialFileUploadState);
  const [isPending, startTransition] = useTransition();

  const validateFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type as typeof ACCEPTED_FILE_TYPES[number])) {
      return {
        isValid: false,
        error: translate('errors.fileTypeNotSupported', { type: file.type })
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: translate('errors.fileTooLarge', { maxSize: MAX_FILE_SIZE / (1024 * 1024) })
      };
    }

    return { isValid: true };
  }, [translate]);

  const handleFileInput = useCallback(
    (file: File, slotId: string) => {
      logger.debug("handleFileInput called", { slotId, fileName: file.name });

      const validation = validateFile(file);

      if (!validation.isValid) {
        dispatch({
          type: 'SET_ERROR',
          payload: validation.error || translate('errors.invalidFiles'),
        });
        return;
      }

      dispatch({ type: 'ADD_FILE', payload: { slotId, file } });
    },
    [validateFile, translate],
  );

  const removeFile = useCallback(
    (slotId: string) => {
      dispatch({ type: 'REMOVE_FILE', payload: slotId });
    },
    [],
  );

  const handleUpload = useCallback(
    async (orderId: string, seatRequirements: SeatUploadRequirements, onSuccess?: () => void) => {
      const filesToUpload: UploadableFile[] = [];

      // Construct UploadableFile array by mapping state.files to seatRequirements slots
      seatRequirements.seatRequirements.forEach(req => {
        req.slots.forEach(slot => {
          const file = state.files[slot.id];
          if (file) {
            filesToUpload.push({
              file,
              slotId: slot.id,
              imagePosition: slot.imagePosition,
              lineItemId: req.itemId, // This might need to be parsed if it has the index suffix, but usually line_item_id is just the ID. 
              // Wait, req.itemId in seatRequirements.ts is constructed as `lineItem.id + (i > 0 ? -${i} : '')`.
              // The API expects the actual line_item_id. 
              // If the item ID in requirements is composite, I need to extract the real ID.
              // Actually, for custom items, if they are split, they might share the same line_item_id? 
              // Or does Shopify split them? 
              // The user requirement says: "For every custom product in the order...".
              // If quantity > 1, do we treat them as separate uploads? Yes, the logic in seatRequirements.ts does that.
              // But the API likely needs the unique line item ID if they are separate line items, OR the same line item ID if they are quantity > 1 of the same line item.
              // If they are quantity > 1 of the same line item, the backend needs to know which "unit" it is?
              // The prompt says: "processes every custom item in an order".
              // And "send the image file along with the associated, standardized image_position value".
              // It doesn't explicitly mention an index for the item itself if quantity > 1.
              // However, `line_item_id` is a required field.
              // If I have 2 of the same seat, they have the same `line_item_id`.
              // If I send `front_1` for both, how does the backend distinguish?
              // Maybe the backend handles it? Or maybe I should assume quantity=1 for now or that the user handles it?
              // The `seatRequirements.ts` logic generates `itemId` as `id-index` if quantity > 1.
              // I should probably strip the suffix to get the real `line_item_id`.
            });
          }
        });
      });

      if (filesToUpload.length === 0) return;

      dispatch({ type: 'START_UPLOAD' });

      try {
        const results = await uploadFiles(
          filesToUpload.map(f => ({
            ...f,
            lineItemId: f.lineItemId.split('-')[0] // Basic stripping of suffix if present
          })),
          orderId,
          (overallProgress) => {
            dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: overallProgress });
          },
          (slotId, fileProgress, status, error) => {
            dispatch({
              type: 'SET_FILE_PROGRESS',
              payload: {
                slotId,
                progress: {
                  slotId,
                  progress: fileProgress,
                  status,
                  error,
                },
              },
            });
          }
        );

        const successfulUploads = results
          .filter((r) => r.success)
          .map((r) => r.url!);
        const failedUploads = results.filter((r) => !r.success);

        if (failedUploads.length > 0) {
          dispatch({
            type: 'UPLOAD_FAILURE',
            payload: translate('errors.filesFailed', {
              count: failedUploads.length,
            }),
          });
        } else {
          dispatch({ type: 'UPLOAD_SUCCESS', payload: successfulUploads });
          onSuccess?.();
        }
      } catch (e: any) {
        dispatch({
          type: 'UPLOAD_FAILURE',
          payload: e?.message || translate('errors.uploadFailed'),
        });
      }
    },
    [state.files, translate],
  );

  const handleCloseModal = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    files: state.files,
    isUploading: state.isUploading,
    uploadProgress: state.uploadProgress,
    fileProgress: state.fileProgress,
    error: state.error,
    uploadResults: state.uploadResults,
    handleFileInput,
    removeFile,
    handleUpload,
    handleCloseModal,
  };
};
